

/*
  Version: v01a
  File: CertifiedBankCardHomePage_v01.tsx
  Description: Home / landing page split into reusable components with legal links below the submit button.
*/

import React, { ReactNode, useState } from 'react';
import { Alert, Button, Card, Carousel, Col, Container, Form, ListGroup, Modal, Row, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import CertifiedBankCardHeader_v01 from './CertifiedBankCardHeader_v01';
import CertifiedBankCardFooter_v01 from './CertifiedBankCardFooter_v01';
import { merchantTypes } from '../MerchantTypesListPopup_v01';
import { LogoText } from './CertifiedBankCardUtility';

// const STANDARD_AWS_API_URL =
//   import.meta.env.VITE_STANDARD_AWS_API_URL ||
//   import.meta.env.VITE_CONTACT_API_URL ||
//   '';

import { MASTER_CONFIG_OPTIONS } from "../../00MasterConfig/MasterSystemConfig";
const STANDARD_AWS_API_URL = MASTER_CONFIG_OPTIONS.API_URL;

type ContactFormState = {
  businessName: string;
  contactName: string;
  phoneNumber: string;
  emailAddress: string;
  zipCode: string;
  paymentProcessing: boolean;
  businessFunding: boolean;
  financialServices: boolean;
  otherServices: boolean;
  monthlyVolume: string;
  businessType: string;
  businessMessage: string;
  smsMarketingConsent: boolean;
  smsNonMarketingConsent: boolean;
};

const initialFormState: ContactFormState = {
  businessName: '',
  contactName: '',
  phoneNumber: '',
  emailAddress: '',
  zipCode: '',
  paymentProcessing: false,
  businessFunding: false,
  financialServices: false,
  otherServices: false,
  monthlyVolume: 'Under $10,000',
  businessType: '',
  businessMessage: '',
  smsMarketingConsent: false,
  smsNonMarketingConsent: false,
};

export type CertifiedBankCardCarouselSlide = {
  id: string;
  imageUrl: string;
  alt: string;
  title: ReactNode;
  subtitle: ReactNode;
};

// const [submittedSummary, setSubmittedSummary] = useState<ContactFormState | null>(null);



export const certifiedBankCardCarouselSlides: CertifiedBankCardCarouselSlide[] = [
  {
    id: 'slide-01',
    imageUrl: 'https://images.unsplash.com/photo-1556740749-887f6717d7e4?auto=format&fit=crop&w=1600&q=80',
    alt: 'Business owner reviewing payment options at a counter',
    title: <>Payment Processing That Works for Businesses</>,
    subtitle: <>
      Powerful solutions for retail, restaurant, eCommerce, field services, digital services, and mobile businesses that want reliable payments, more value and better support.
    </>,
  },
  {
    id: 'slide-02',
    imageUrl: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1600&q=80',
    alt: 'Financial paperwork and calculator on a desk',
    title: <>Flexible Business Funding Options to Support Growth</>,
    subtitle: <>
      Explore business loans and working capital options for inventory, equipment, payroll, expansion, and short-term cash flow needs.
    </>,
  },
  {
    id: 'slide-03',
    imageUrl: 'https://images.unsplash.com/photo-1520607162513-77705c0f0d4a?auto=format&fit=crop&w=1600&q=80',
    alt: 'Restaurant payment terminal at checkout',
    title: <>Modern Merchant Services reduce friction and win lifetime customers</>,
    subtitle: <>
      We help small and medium sized businesses find the best solutions, improve operations, accellerate payments and grow their business.
    </>,
  },
  {
    id: 'slide-04',
    imageUrl: 'https://images.unsplash.com/photo-1597155135675-d4cef690184a?q=80&w=889&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    alt: 'Business team discussing growth around a table',
    title: <>Built for Business Owners who need lower costs, higher profits and reliable systems</>,
    subtitle: <>
      As a direct payment processer, we deliver exceptional services and much better pricing.
    </>,
  },
  {
    id: 'slide-05',
    imageUrl: 'https://images.unsplash.com/photo-1554224154-26032ffc0d07?auto=format&fit=crop&w=1600&q=80',
    alt: 'Business team discussing growth around a table',
    title: <>For Business Owners Who Need Straight Answers</>,
    subtitle: <>
      Clear communication, responsive support, and solutions tailored to your volume, industry, and business goals.
    </>,
  },
  {
    id: 'slide-06',
    imageUrl: 'https://images.unsplash.com/photo-1559526324-593bc073d938?auto=format&fit=crop&w=1600&q=80',
    alt: 'Customer tapping a card for payment',
    title: <>Start with a Review of Your Current Setup</>,
    subtitle: <>
      Request a consultation to discuss your current processor, monthly volume, funding needs, and next best steps.
    </>,
  },
];

const benefitCards = [
  {
    title: 'Payment Processing',
    description:
      'Competitive merchant services for credit cards, debit cards, in-person sales, mobile transactions, and customer-friendly checkout experiences.',
  },
  
  {
  title: 'POS Selection',
  description:
    'Find the right point-of-sale system for your business with solutions tailored for retail, restaurants, mobile payments, and seamless in-person or online transactions.',
  },
  
  {
    title: 'Business Funding',
    description:
      'Funding options for qualified businesses that need working capital to support operations, equipment, staffing, or expansion.  We offer a host of options including next day funding, no credit check, low FICO/Stated income, and other optons to best fit your situation and requirements',
  },
  {
    title: 'Service with Clarity',
    description:
      'A consultative approach focused on helping business owners evaluate their options with clarity and decide with confidence..',
  },
];

const industries = [
  'Retail Stores',
  'Restaurants & Cafes',
  'Salons & Spas',
  'Auto Services',
  'Contractors',
  'Nursery / Garden Center',
  'Farm Supply Retailers',
  'Medical Practices',
  'Dental Practices',
  'Professional Services',
  'Wholesale Clubs',
  'eCommerce Brands',
];

const advantages = [
  'Built for small and medium sized businesses',
  'Fast, easy setup process',
  'Flexible solutions based on business type',
  'Support for payment processing and funding',
  'Improved, more reliable operations',
  'Better value, better pricing',
];

export default function CertifiedBankCardHomePage_v02(): React.JSX.Element {
  const [show, setShow] = useState(false);
  const [formData, setFormData] = useState<ContactFormState>(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { id, value, type } = event.target;
    const checked = type === 'checkbox' ? (event.target as HTMLInputElement).checked : undefined;

    setFormData((current) => ({
      ...current,
      [id]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitStatus('idle');
    setSubmitMessage('');

    if (!STANDARD_AWS_API_URL) {
      setSubmitStatus('error');
      setSubmitMessage('The contact API endpoint is not configured.');
      return;
    }

    if (!formData.businessName || !formData.contactName || !formData.emailAddress) {
      setSubmitStatus('error');
      setSubmitMessage('Please enter your business name, contact name, and email address.');
      return;
    }

    const servicesInterestedIn = [
      formData.paymentProcessing ? 'Payment Processing' : null,
      formData.businessFunding ? 'Business Funding' : null,
      formData.financialServices ? 'Financial Services' : null,
      formData.otherServices ? 'Other' : null,
    ].filter(Boolean);


    const payload = {
      // Standard Fields
      source: 'CertifiedBankCard.com',
      formType: 'contact-request',
      submittedAt: new Date().toISOString(),

      // Domain / Client Information
      domainName: window.location.hostname,
      fullURL: window.location.href,
      pagePath: window.location.pathname,
      protocol: window.location.protocol,
      referrer: document.referrer || '',

      // Browser / Device Information
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,

      // Contact Information
      businessName: formData.businessName,
      contactName: formData.contactName,
      phoneNumber: formData.phoneNumber,
      emailAddress: formData.emailAddress,
      zipCode: formData.zipCode,

      // Business Details
      servicesInterestedIn,
      monthlyVolume: formData.monthlyVolume,
      businessType: formData.businessType,
      message: formData.businessMessage,

      // Service Flags
      paymentProcessing: formData.paymentProcessing,
      businessFunding: formData.businessFunding,
      financialServices: formData.financialServices,
      otherServices: formData.otherServices,

      // SMS Compliance
      smsMarketingConsent: formData.smsMarketingConsent,
      smsNonMarketingConsent: formData.smsNonMarketingConsent,
      smsConsentProvided:
        formData.smsMarketingConsent ||
        formData.smsNonMarketingConsent,

      smsMarketingConsentText:
        'I consent to receive marketing text messages from CertifiedBankCard.com at the phone number provided. Message frequency may vary. Message and data rates may apply. Reply STOP to opt out and HELP for help.',

      smsNonMarketingConsentText:
        'I consent to receive non-marketing text messages from CertifiedBankCard.com about my request, appointment reminders, account updates, and service follow-up. Message frequency may vary. Message and data rates may apply. Reply STOP to opt out and HELP for help.',

      smsConsentDisclosure:
        'SMS consent is optional, not required to submit this form, and not a condition of purchase.',
    };


    const payload_v01_original = {
      source: 'CertifiedBankCard.com',
      formType: 'contact-request',
      submittedAt: new Date().toISOString(),
      businessName: formData.businessName,
      contactName: formData.contactName,
      phoneNumber: formData.phoneNumber,
      emailAddress: formData.emailAddress,
      zipCode: formData.zipCode,
      servicesInterestedIn,
      monthlyVolume: formData.monthlyVolume,
      businessType: formData.businessType,
      message: formData.businessMessage,
      smsMarketingConsent: formData.smsMarketingConsent,
      smsNonMarketingConsent: formData.smsNonMarketingConsent,
      smsConsentProvided: formData.smsMarketingConsent || formData.smsNonMarketingConsent,
      smsMarketingConsentText:
        'I consent to receive marketing text messages from CertifiedBankCard.com at the phone number provided. Message frequency may vary. Message and data rates may apply. Reply STOP to opt out and HELP for help.',
      smsNonMarketingConsentText:
        'I consent to receive non-marketing text messages from CertifiedBankCard.com about my request, appointment reminders, account updates, and service follow-up. Message frequency may vary. Message and data rates may apply. Reply STOP to opt out and HELP for help.',
      smsConsentDisclosure:
        'SMS consent is optional, not required to submit this form, and not a condition of purchase.',
    };








    try {
      setIsSubmitting(true);
      // console.log("apiurl:", STANDARD_AWS_API_URL);

      const response = await fetch(STANDARD_AWS_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      setSubmitStatus('success');
      setSubmitMessage('Thank you. Your request has been submitted.');
      setFormData(initialFormState);
    } catch (error) {
      console.error(error);
      setSubmitStatus('error');
      setSubmitMessage('There was a problem submitting the form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div id="top" className="bg-white text-dark">
      <CertifiedBankCardHeader_v01 />

      <style>{`
        .cbc-hero-carousel,
        .cbc-hero-carousel .carousel-inner,
        .cbc-hero-carousel .carousel-item {
          min-height: 75vh;
        }

        .cbc-sectionImage{
          backgroundImage: 'linear-gradient(rgba(8, 32, 66, 0.75), rgba(13, 110, 253, 0.65)), url(/images-CertifiedBankcard/AdobeStock_449148630_Preview-global.jpeg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          width: '100%',
          
        }

        .cbc-slide {
          position: relative;
          min-height: 75vh;
          background-size: cover;
          background-position: center;
          display: flex;
          align-items: center;
        }

        // .cbc-slide::before {
        //   content: '';
        //   position: absolute;
        //   inset: 0;
        //   background: linear-gradient(135deg, rgba(5, 18, 41, 0.88), rgba(16, 73, 148, 0.62));
        // }
 
 
        // GOOD STANDARD OVERLAY 
        // .cbc-slide::before {
        //   content: '';
        //   position: absolute;
        //   inset: 0;
        //   background: rgba(0, 0, 0, 0.9);
        // }

        .cbc-slide::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(0, 0, 0, 0.70), rgba(0, 0, 0, 0.62));
        }


        /*
          Carousel overlay options for developers:
          Option 1 (current): gradient overlay
          .cbc-slide::before { background: linear-gradient(135deg, rgba(5, 18, 41, 0.88), rgba(16, 73, 148, 0.62)); }

          Option 2: darker solid overlay
          .cbc-slide::before { background: rgba(8, 32, 66, 0.68); }

          Option 3: lighter blue gradient
          .cbc-slide::before { background: linear-gradient(135deg, rgba(13, 110, 253, 0.48), rgba(8, 32, 66, 0.38)); }

          Option 4: no overlay
          .cbc-slide::before { background: transparent; }

          Option 5: subtle dark fade from left to right
          .cbc-slide::before { background: linear-gradient(90deg, rgba(8, 32, 66, 0.72) 0%, rgba(8, 32, 66, 0.24) 100%); }
        */

        .cbc-slide-content {
          position: relative;
          z-index: 2;
          width: 100%;
        }

        .cbc-overlay-card {
          background: rgba(255, 255, 255, 0.96);
          border: none;
          border-radius: 1.5rem;
          box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.16);
        }

        .cbc-content-section {
          scroll-margin-top: 90px;
        }

        .cbc-section-copy {
          max-width: 760px;
          margin-left: auto;
          margin-right: auto;
          line-height: 1.75;
        }

        .cbc-section-title {
          font-weight: 800;
          letter-spacing: -0.02em;
        }

        .cbc-industry-pill {
          border: 1px solid rgba(13, 110, 253, 0.18);
          border-radius: 999px;
          padding: 0.65rem 1rem;
          background: #f8fbff;
          font-weight: 600;
        }

        .cbc-legal-links {
          text-align: center;
          font-size: 0.95rem;
        }

        .cbc-legal-links a {
          text-decoration: none;
        }

        .cbc-legal-links a:hover {
          text-decoration: underline;
        }

        #smsMarketingConsent,
        #smsNonMarketingConsent {
          border: 3px solid var(--bs-primary);
        }

        .sms-consent-highlight {
          border-color: var(--bs-primary) !important;
          box-shadow: 0 0 0 .3rem rgba(13, 110, 253, 0.18);
        }
      `}</style>

      <Carousel controls indicators fade className="cbc-hero-carousel">
        {certifiedBankCardCarouselSlides.map((slide) => (
          <Carousel.Item key={slide.id} interval={6000}>
            <section
              className="cbc-slide"
              style={{
                backgroundImage: `url(${slide.imageUrl})`,
              }}
            >
              <div className="cbc-slide-content py-5">
                <Container fluid className="px-3 px-md-4 px-xl-5">
                  <Container>
                    <Row className="align-items-center g-4">
                      <Col lg={12}>
                        <h1 className="display-3 fw-bold text-white mb-3">{slide.title}</h1>
                        <p className="lead text-white mb-4" style={{ maxWidth: '760px' }}>
                          {slide.subtitle}
                        </p>
                      </Col>
                    </Row>
                  </Container>
                </Container>
              </div>
            </section>
          </Carousel.Item>
        ))}
      </Carousel>

      <section id="services-overview" className="py-5 cbc-content-section">
        <Container>
          <div className="text-center mb-5">
            <h2 className="display-6 cbc-section-title mb-3">Solutions for Business Growth</h2>
            <p className="text-muted cbc-section-copy">
              CertifiedBankCard.com is designed to help small and medium sized businesses improve payment acceptance, strengthen cash flow, and explore practical funding options.
            </p>
          </div>

          <Row className="g-4">
            {benefitCards.map((card) => (
                                          <Col md={3} key={card.title}>
                                            <Card className="h-100 border-0 shadow-sm rounded-4">
                                              <Card.Body className="p-1">
                                                <h3 className="h4 fw-bold mb-3">{card.title}</h3>
                                                <p className="text-muted mb-0">{card.description}</p>
                                              </Card.Body>
                                            </Card>
                                          </Col>
                                        ))}
          </Row>
        </Container>
      </section>

      <section id="why-businesses-reach-out" className="py-5 bg-light cbc-content-section">
        <Container>
          <Row className="g-4 align-items-center">
            <Col lg={6}>
              <h2 className="display-6 cbc-section-title mb-4">Why Businesses Choose<br></br> <LogoText></LogoText></h2>
              <Row className="g-3">
                {advantages.map((item) => (
                  <Col sm={6} key={item}>
                    <Card className="h-100 border-0 shadow-sm rounded-4">
                      <Card.Body className="p-3">
                        <div className="fw-semibold">{item}</div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Col>

            <Col lg={6}>
              <Card className="border-0 shadow rounded-4">
                <Card.Body className="p-4 p-md-5">
                  <h3 className="fw-bold mb-3">Industries We Commonly Support</h3>
                  <p className="text-muted mb-4">
                    Our services are a strong fit for businesses looking for merchant services, point-of-sale options, or access to capital for ongoing operations and expansion.
                    &nbsp;&nbsp;
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setShow(true);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      All Merchant Types
                    </a>

                    <Modal show={show} onHide={() => setShow(false)}>
                      <Modal.Header closeButton>
                        <Modal.Title>
                          We help wide variety of businesses &nbsp;&nbsp; <i className="bi bi-emoji-smile"></i>
                        </Modal.Title>
                      </Modal.Header>

                      <Modal.Body>
                        <ListGroup variant="flush">
                          {/* 
                          
                          {merchantTypes.map((item) => (
                            <ListGroup.Item key={item.id}>{item.name}</ListGroup.Item>
                          ))} 

                           */}

                          {/* {merchantTypes.map((item) => (
                            <div>{item.name}</div>
                          ))} 
                           
                           */}

                           {merchantTypes.map((item) => (
                              <div key={item.id}>{item.name}</div>
                            ))}
                              

                        </ListGroup>
                      </Modal.Body>

                      <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShow(false)}>
                          Close
                        </Button>
                      </Modal.Footer>
                    </Modal>
                  </p>

                  <div className="d-flex flex-wrap gap-2">
                    {industries.map((industry) => (
                      <div key={industry} className="cbc-industry-pill">
                        {industry}
                      </div>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      <section
        id="industries-served"
        className="py-5 text-white cbc-content-section cbc-sectionImage"
        // style={{ background: 'linear-gradient(135deg, #082042 0%, #0d6efd 100%)'}}
         style={{ 
          
               backgroundImage: `
                linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.3)),
                url("/images-CertifiedBankcard/AdobeStock_449148630_Preview-global.jpeg")
              `,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              width: '100%',
         }} 
      >

        <Container>
          <Row className="align-items-center g-4">
            <Col lg={8}>

              <h2 className="display-6 fw-bold mb-2">Integrated payments in minutes, not months</h2>
              <p className="mb-0 cbc-section-copy ms-lg-0">
                With our unified API and extensive list of pre-certified POS devices from top manufacturers, integrators leverage Payroc’s powerful payments platform to transact millions of simple, seamless payments everywhere, every day.
              </p>


              {/* AdobeStock_449148630_Preview-global */}
            </Col>
            <Col lg={4} className="text-lg-end">
              <div className="fw-semibold">Simple review · Clear options · Fast follow-up</div>
            </Col>
          </Row>
        </Container>
      </section>

      <section id="get-started-form" className="py-5 bg-light cbc-content-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8} xl={7}>
              <Card className="cbc-overlay-card">
                <Card.Body className="p-4 p-md-5">
                  <div className="text-center mb-4">
                    <h2 className="display-6 cbc-section-title mb-2">Get Started</h2>
                    <p className="text-muted mb-0">
                      Tell us about your business and which services you want to explore.
                    </p>
                  </div>

                  {submitStatus !== 'idle' && (
                    <Alert variant={submitStatus === 'success' ? 'success' : 'danger'}>
                      {submitMessage}
                    </Alert>
                  )}

                  <Form onSubmit={handleSubmit}>
                    <Row className="g-3">
                      <Col xs={12}>
                        <Form.Group controlId="businessName">
                          <Form.Label>Business Name</Form.Label>
                          <Form.Control
                            required
                            type="text"
                            placeholder="ABC Business LLC"
                            value={formData.businessName}
                            onChange={handleInputChange}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group controlId="contactName">
                          <Form.Label>Contact Name</Form.Label>
                          <Form.Control
                            required
                            type="text"
                            placeholder="Jane Smith"
                            value={formData.contactName}
                            onChange={handleInputChange}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group controlId="phoneNumber">
                          <Form.Label>Phone Number</Form.Label>
                          <Form.Control
                            type="tel"
                            placeholder="(555) 555-5555"
                            value={formData.phoneNumber}
                            onChange={handleInputChange}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={8}>
                        <Form.Group controlId="emailAddress">
                          <Form.Label>Email Address</Form.Label>
                          <Form.Control
                            required
                            type="email"
                            placeholder="name@company.com"
                            value={formData.emailAddress}
                            onChange={handleInputChange}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={4}>
                        <Form.Group controlId="zipCode">
                          <Form.Label>Zip Code</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="80112"
                            value={formData.zipCode}
                            onChange={handleInputChange}
                          />
                        </Form.Group>
                      </Col>

                      <Col xs={12}>
                        <Form.Group>
                          <Form.Label className="fw-semibold">Services Interested In</Form.Label>
                          <div className="border rounded-3 p-3 bg-white">
                            <Row className="g-2">
                              <Col md={6}>
                                <Form.Check
                                  id="paymentProcessing"
                                  type="checkbox"
                                  label="Payment Processing"
                                  checked={formData.paymentProcessing}
                                  onChange={handleInputChange}
                                />
                              </Col>
                              <Col md={6}>
                                <Form.Check
                                  id="businessFunding"
                                  type="checkbox"
                                  label="Business Funding"
                                  checked={formData.businessFunding}
                                  onChange={handleInputChange}
                                />
                              </Col>
                              <Col md={6}>
                                <Form.Check
                                  id="financialServices"
                                  type="checkbox"
                                  label="Financial Services"
                                  checked={formData.financialServices}
                                  onChange={handleInputChange}
                                />
                              </Col>
                              <Col md={6}>
                                <Form.Check
                                  id="otherServices"
                                  type="checkbox"
                                  label="Other"
                                  checked={formData.otherServices}
                                  onChange={handleInputChange}
                                />
                              </Col>
                            </Row>
                          </div>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group controlId="monthlyVolume">
                          <Form.Label>Monthly Volume</Form.Label>
                          <Form.Select
                            value={formData.monthlyVolume}
                            onChange={handleInputChange}
                          >
                            <option>Under $10,000</option>
                            <option>$10,000 - $25,000</option>
                            <option>$25,000 - $75,000</option>
                            <option>$75,000+</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group controlId="businessType">
                          <Form.Label>Business Type</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Retail, restaurant, contractor, medical, etc."
                            value={formData.businessType}
                            onChange={handleInputChange}
                          />
                        </Form.Group>
                      </Col>

                      <Col xs={12}>
                        <Form.Group controlId="businessMessage">
                          <Form.Label>How Can We Help?</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={5}
                            placeholder="Share your current setup, goals, or funding needs."
                            value={formData.businessMessage}
                            onChange={handleInputChange}
                          />
                        </Form.Group>
                      </Col>

                      <Col xs={12}>
                        <div
                          className={
                            formData.phoneNumber &&
                            !formData.smsMarketingConsent &&
                            !formData.smsNonMarketingConsent
                              ? 'sms-consent-highlight border rounded p-3 bg-white'
                              : 'border rounded p-3 bg-white'
                          }
                        >
                          <Form.Check
                            id="smsMarketingConsent"
                            type="checkbox"
                            checked={formData.smsMarketingConsent}
                            onChange={handleInputChange}
                            label="I consent to receive marketing text messages from CertifiedBankCard.com at the phone number provided. Message frequency may vary. Message and data rates may apply. Reply STOP to opt out and HELP for help."
                          />

                          <Form.Check
                            id="smsNonMarketingConsent"
                            type="checkbox"
                            className="mt-3"
                            checked={formData.smsNonMarketingConsent}
                            onChange={handleInputChange}
                            label="I consent to receive non-marketing text messages from CertifiedBankCard.com about my request, appointment reminders, account updates, and service follow-up. Message frequency may vary. Message and data rates may apply. Reply STOP to opt out and HELP for help."
                          />

                          <div className="small text-muted mt-3">
                            SMS consent is optional, not required to submit this form, and not a condition of purchase.
                          </div>
                        </div>
                      </Col>

                      <Col xs={12}>
                        <div className="small text-muted border rounded p-3 bg-white">
                          By submitting this form, you agree to be contacted regarding payment processing, business funding, and related financial services.
                        </div>
                      </Col>

                      <Col xs={12} className="d-grid">
                        <Button variant="primary" size="lg" type="submit" disabled={isSubmitting}>
                          {isSubmitting ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Submitting...
                            </>
                          ) : (
                            'Submit Request'
                          )}
                        </Button>
                      </Col>

                      <Col xs={12}>
                        <div className="text-center small text-muted">
                          We respect your inbox. You can opt out anytime. For SMS, reply STOP to unsubscribe.
                          <br />
                          <strong>
                            <em>We will not sell your data, pester you or spam you.</em>
                          </strong>
                        </div>
                      </Col>

                      <Col xs={12}>
                        <div className="cbc-legal-links">
                          <Link to="/terms-of-service">Terms of Service</Link>
                          <span className="mx-2 text-muted">|</span>
                          <Link to="/privacy-policy">Privacy Policy</Link>
                        </div>
                      </Col>
                    </Row>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      <CertifiedBankCardFooter_v01 />
    </div>
  );
}

/*
  File Name: CertifiedBankCardHomePage_v01.tsx
  Quick Description: Split home page component with the original landing page content plus legal links below the submit button.
  Version: v01a
*/